import Button from "@common/components/Button";
import Grid from '@mui/material/Grid2';
import ConfirmDialog from "@common/components/Button/DeleteButton/dialog";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import {
  NotificationContext,
  useErrorNotification,
} from "@common/components/Notification";
import RightDrawer from "@common/components/RightDrawer";
import SlideUnsaved from "@common/components/SlideUnsaved";
import mappers from "@common/utils/filters/mappers";
import {
  getTimesForTicketsFilter,
  TIME_FILTER_SEARCH_KEY,
} from "@common/utils/filters/mappers/tickets";
import { AccountDetailContext } from "@pages/Assets/Account/context";
import { LocationDetailContext } from "@pages/Assets/Location/context";
import { SelectedTicket } from "@pages/Tickets/context";
import CloseCaseContent from "@pages/Tickets/Drawer/CloseCaseDialog/CloseCaseContent";
import {
  CloseCaseTicketActionContext,
  CloseCaseTicketContext,
  ErrorIssueActionContext,
} from "@pages/Tickets/Drawer/CloseCaseDialog/context";
import { getCommonFields, getIssue } from "@services/account";
import { IssueName } from "@services/account/types";
import { closeTickets } from "@services/tickets";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

type CloseTicketDrawerProps = {
  open: boolean;
  onClose: (closedTickets: boolean) => void;
  tickets: number[] | SelectedTicket[] | undefined;
  totalTickets: number | undefined;
  parentId: string | string[];
  parentEntityClass: string;
  filters: FilterDefinition[];
};

const isTicketIds = (x: SelectedTicket[] | number[]): x is number[] =>
  typeof x[0] === typeof 0;
const CloseTicketDrawer = (
  props: React.PropsWithChildren<CloseTicketDrawerProps>
) => {
  const { t } = useTranslation();
  const {
    open,
    onClose,
    tickets,
    totalTickets,
    parentId,
    parentEntityClass,
    filters,
  } = props;
  const ticketIds =
    tickets === undefined
      ? undefined
      : isTicketIds(tickets)
      ? tickets
      : tickets.map((x) => x.id);

  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const { dispatchNotification } = useContext(NotificationContext);
  const [searchParams] = useSearchParams();

  const { detail } = useContext(RoomsRealtimeContext);
  const location = useContext(LocationDetailContext);
  const account = useContext(AccountDetailContext);

  const { resolution, source, comment, isFormModified } = useContext(
    CloseCaseTicketContext
  );
  const { setResolution, setSource, setComment, restoreInitialState } =
    useContext(CloseCaseTicketActionContext);

  const dispatchError = useErrorNotification();
  const { setErrorIssueResolution, setErrorIssueSource } = useContext(
    ErrorIssueActionContext
  );

  const count = (ticketIds?.length ?? 0) > 0 ? ticketIds?.length : totalTickets;
  useEffect(() => {
    const accountId =
      detail?.account?.id ??
      detail?.room?.location?.account?.id ??
      account?.id ??
      location?.account?.id ??
      searchParams.get("accountId") ??
      undefined;
    if (open) {
      if (accountId) {
        (async () => {
          let issueSources = await getIssue(accountId, "source" as IssueName);
          let issueResolutions = await getIssue(
            accountId,
            "resolution" as IssueName
          );
          setSource({
            issueSources,
            issueSource: "",
          });
          setResolution({
            issueResolutions,
            issueResolution: "",
          });
          setComment("");
        })();
      } else {
        if (tickets !== undefined && !isTicketIds(tickets)) {
          (async () => {
            try {
              const commons = await getCommonFields(
                Object.keys(
                  (Array.isArray(parentId) ? parentId : [parentId]).reduce(
                    (acc, value) => ({ ...acc, [value]: true }),
                    {}
                  )
                )
              );

              setSource({
                issueSources: commons.source,
                issueSource: "",
              });
              setResolution({
                issueResolutions: commons.resolution,
                issueResolution: "",
              });
              setComment("");
              if (commons.source?.length == 0)
                throw new Error(
                  t("No common Source value found between selected Accounts")
                );
              if (commons.resolution?.length == 0)
                throw new Error(
                  t(
                    "No common Resolution value found between selected Accounts"
                  )
                );
            } catch (e) {
              dispatchError(e);
            }
          })();
        }
      }
    }
  }, [open]);

  const validateForm = () => {
    setErrorIssueResolution("");
    setErrorIssueSource("");

    if (resolution.issueResolution !== "" && source.issueSource !== "") {
      setOpenConfirmDialog(true);
    } else {
      if (resolution.issueResolution === "") {
        setErrorIssueResolution(t("Field is required"));
      }
      if (source.issueSource === "") {
        setErrorIssueSource(t("Field is required"));
      }
    }
  };

  const onConfirmCloseTicket = async () => {
    try {
      const timeFilterType = decodeURI(
        searchParams.get(TIME_FILTER_SEARCH_KEY) ?? ""
      );
      const allFilters = mappers.Tickets.fromDefinition.toSL(filters);
      let {
        ["start.after"]: startDate,
        ["end.before"]: endDate,
        ...slFilters
      } = allFilters;
      if (timeFilterType !== "" && timeFilterType != "Custom time period") {
        [startDate, endDate] = getTimesForTicketsFilter(
          timeFilterType as TicketsTimeFilter
        );
      }

      const isClosingFiltered = ticketIds?.length === 0;
      const isCloseAll =
        isClosingFiltered && Object.entries(allFilters).length === 0;
      let entityKey: keyof typeof allFilters | undefined;
      switch (parentEntityClass) {
        case "DEVICE":
          entityKey = "device.id";
          break;
        case "ROOM":
          entityKey = "room.id";
          break;
        case "ACCOUNT":
          entityKey = "accountId";
          break;
      }
      await closeTickets({
        parentId: parentId,
        parentEntityClass: parentEntityClass,
        closeAll: isCloseAll,
        resolution: resolution.issueResolution,
        source: source.issueSource,
        comment: comment,
        startDate,
        endDate,
        ...(isCloseAll
          ? {}
          : isClosingFiltered
          ? {
              filters: {
                ...slFilters,
                ...(entityKey
                  ? {
                      [entityKey]: Array.isArray(parentId)
                        ? parentId
                        : [parentId],
                    }
                  : {}),
              },
            }
          : { ticketsIds: ticketIds }),
      });

      restoreInitialState();
      detail?.refetch();
      onClose(true);
      dispatchNotification({
        title: t("Success"),
        severity: "success",
        message: t("Tickets successfully closed"),
      });
    } catch (e) {
      dispatchError(e);
    }
  };
  const getTicketMessage = (count: number) => {
    if (count === 0) {
      return {
        title: t("Close all tickets ({{count}})", { count: totalTickets }),
        actionLabel: "Close tickets",
        confirmPrompt: t("Are you sure you want to close {{count}} tickets?", {
          count: totalTickets,
        }),
      };
    } else if (count === 1) {
      return {
        title: t("Close selected ticket ({{count}})", { count }),
        actionLabel: "Close selected ticket",
        confirmPrompt: t("Are you sure you want to close {{count}} ticket?", {
          count,
        }),
      };
    } else {
      return {
        title: t("Close selected tickets ({{count}})", { count }),
        actionLabel: "Close selected tickets",
        confirmPrompt: t("Are you sure you want to close {{count}} tickets?", {
          count,
        }),
      };
    }
  };
  const { title, actionLabel, confirmPrompt } = getTicketMessage(
    ticketIds?.length ?? 0
  );

  const handleClose = () => {
    onClose(false);
    restoreInitialState();
    setShowWarning(false);
  };

  const handleCloseWithWarning = () => {
    if (isFormModified) {
      setShowWarning(true);
    } else {
      handleClose();
    }
  };

  return (
    <RightDrawer
      width="600px"
      open={open}
      onClose={handleCloseWithWarning}
      closeButton={
        <Grid
          sx={(theme) => ({
            "&>.MuiButton-root": { marginRight: theme.spacing(1) },
          })}>
          <Button variant="contained" color="white" onClick={validateForm}>
            {t(`Close Ticket${count === 1 ? "" : "s"}`)}
          </Button>
          <Button color="greyed" variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
        </Grid>
      }
      title={title}
    >
      <ConfirmDialog
        actionLabel={actionLabel}
        confirmPrompt={confirmPrompt}
        onConfirm={() => {
          setOpenConfirmDialog(false);
          onConfirmCloseTicket();
        }}
        onClose={() => setOpenConfirmDialog(false)}
        open={openConfirmDialog}
      />
      <Grid
        container
        direction="column"
        sx={{ width: '100%', alignItems: "center",
          gap: "1.5rem",
          paddingTop: "1rem",
          px: 2 }}>
        <CloseCaseContent />
      </Grid>
      <SlideUnsaved
        direction="left"
        warning={showWarning}
        onClose={handleClose}
        onCancel={() => setShowWarning(false)}
      />
    </RightDrawer>
  );
};

export default CloseTicketDrawer;
